import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { CircleAlert } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import { INSET_CLASS } from '@/components/ui/data-table'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { NumberCell } from './TableCells'
import type { MeasureGroup } from '../hooks/useMeasureStats'
import { formatPercent } from '../utils/format'

/**
 * A staggered breakdown: one section per measure, each listing its values
 * biggest first with the share they took of that measure. Sections are what
 * makes a single count readable — impressions per OS only mean something
 * against the other OSes, not against the device types above them.
 */
export interface ShareBreakdownTableProps {
  groups: MeasureGroup[]
  /** Section heading for a measure key, e.g. `deviceType` → "Device". */
  groupLabel: (key: string) => string
  /** Header over the count column — what the numbers are counting. */
  valueLabel: string
  loading?: boolean
  error?: Error | null
  emptyMessage: string
  /** Caps the rows' height in px so the card does not outgrow the page. */
  maxHeight?: number
}

/** The row's share of its section, as a bar the eye can scan down. */
function ShareBar({ share }: { share: number }) {
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-16 shrink-0 overflow-hidden rounded-full bg-(--ad-neutral-soft)">
        <div className="h-full rounded-full bg-(--ad-primary)" style={{ width: `${share}%` }} />
      </div>
      <span className="text-xs tabular-nums text-(--ad-text-muted)">{formatPercent(share, 1)}</span>
    </div>
  )
}

export function ShareBreakdownTable({
  groups,
  groupLabel,
  valueLabel,
  loading,
  error,
  emptyMessage,
  maxHeight,
}: ShareBreakdownTableProps) {
  const { t } = useTranslation()

  if (error) {
    return (
      <div className="flex items-center gap-2 px-(--card-spacing) py-4 text-destructive">
        <CircleAlert className="size-4" />
        <span className="text-sm">{error.message}</span>
      </div>
    )
  }

  const stickyHead = maxHeight ? 'sticky top-0 z-10' : undefined
  const scroll = maxHeight
    ? { containerClassName: 'overflow-y-auto', containerStyle: { maxHeight } }
    : {}

  return (
    <Table className={INSET_CLASS} {...scroll}>
      <TableHeader>
        <TableRow>
          {/* Sticky lives on the cells: a sticky `tr` is not honoured. */}
          <TableHead className={cn('text-xs text-(--ad-text-muted)', stickyHead)}>
            {t('stats.value')}
          </TableHead>
          <TableHead className={cn('w-40 text-xs text-(--ad-text-muted)', stickyHead)}>
            {t('stats.share')}
          </TableHead>
          <TableHead className={cn('w-28 text-right text-xs text-(--ad-text-muted)', stickyHead)}>
            {valueLabel}
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {loading &&
          Array.from({ length: 6 }).map((_, index) => (
            <TableRow key={index}>
              <TableCell colSpan={3}>
                <Skeleton className="h-4 w-full" />
              </TableCell>
            </TableRow>
          ))}
        {!loading && groups.length === 0 && (
          <TableRow>
            <TableCell colSpan={3} className="py-8 text-center text-sm text-(--ad-text-muted)">
              {emptyMessage}
            </TableCell>
          </TableRow>
        )}
        {!loading &&
          groups.map((group) => (
            <Fragment key={group.key}>
              <TableRow className="bg-(--ad-neutral-soft) hover:bg-(--ad-neutral-soft)">
                <TableCell
                  colSpan={2}
                  className="py-1.5 text-[11px] font-semibold tracking-wide text-(--ad-text-muted) uppercase"
                >
                  {groupLabel(group.key)}
                </TableCell>
                <TableCell className="py-1.5 text-right">
                  <NumberCell value={group.total} />
                </TableCell>
              </TableRow>
              {group.rows.map((row) => (
                <TableRow key={`${group.key}:${row.key}`}>
                  <TableCell className="pl-6! text-sm font-medium text-(--ad-text)">
                    {row.label}
                  </TableCell>
                  <TableCell>
                    <ShareBar share={row.share} />
                  </TableCell>
                  <TableCell className="text-right">
                    <NumberCell value={row.value} />
                  </TableCell>
                </TableRow>
              ))}
            </Fragment>
          ))}
      </TableBody>
    </Table>
  )
}
